import * as ThumbmarkJS from '@thumbmarkjs/thumbmarkjs'
import cfg from '@root/config'
import { getInstance } from '@/auth'

declare function ym(counterId: number, event: string, ...args: any[]): void

// Единый источник id счётчика Метрики (зависит от окружения, см. config.getYMId)
const YMID = cfg.yMId

let cachedFingerprint: string | null = null

async function getFingerprint(): Promise<string> {
    if (!cachedFingerprint) {
        cachedFingerprint = await ThumbmarkJS.getFingerprint()
    }
    return cachedFingerprint
}

function getProjectId(): string {
    const hostname = window.location.hostname
    const pathname = window.location.pathname

    if (hostname.includes('app.dataheroes') || hostname.includes('app.crmmsk')) {
        const match = pathname.match(/\/project\/([A-Z0-9]+)/i)
        if (match && match[1]) {
            return match[1]
        }
    }
    return hostname.replace(/^www\./, '')
}

// Общая обвязка для целей: счётчик может быть не загружен (блокировщики),
// поэтому любая ошибка гасится — аналитика не должна ронять интерфейс.
export async function sendGoal(goal: string, payload: Record<string, any> = {}) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()

        ym(YMID, 'reachGoal', goal, {
            projectId: getProjectId(),
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            ...payload,
        })

        console.log(`[YM Logged] ${goal}`, payload)
    } catch (e) {
        console.error(`Ошибка логирования ${goal} в Яндекс Метрику:`, String(e))
    }
}

// Привязывает визиты в Метрике к пользователю.
// В setUserID кладём email — его удобно узнавать глазами в отчётах/Вебвизоре.
// Стабильный auth `sub` держим в userParams как якорь на случай смены почты
// (ПДн: email виден всем с доступом к счётчику — это осознанный компромисс ради читаемости).
// Вызывать один раз после авторизации, когда доступен user (идемпотентно).
export function identifyUser(user?: { sub?: string; email?: string; role?: string } | null) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }
        const userId = user?.email ?? user?.sub
        if (!userId) {
            return
        }

        ym(YMID, 'setUserID', userId)
        ym(YMID, 'userParams', {
            email: user?.email ?? null,
            sub: user?.sub ?? null,
            role: user?.role ?? null,
            projectId: getProjectId(),
        })

        console.log(`[YM Logged] identifyUser: ${userId}, role=${user?.role ?? 'unknown'}`)
    } catch (e) {
        console.error('Ошибка идентификации пользователя в Яндекс Метрику:', String(e))
    }
}

// Клик по кнопке промо-акции SMS DH (Мультичат). goalId — идентификатор JS-цели в Метрике,
// совпадает с тем, что заведено в кабинете (например dh_sms_promo_activate). Счётчик берётся
// по окружению из YMID, поэтому один и тот же id работает и на тесте, и на проде.
export function logSmsPromoButton(goalId: string) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }
        ym(YMID, 'reachGoal', goalId, { projectId: getProjectId() })
        console.log(`[YM Logged] sms promo button: ${goalId}`)
    } catch (e) {
        console.error('Ошибка логирования кнопки промо SMS в Яндекс Метрику:', String(e))
    }
}

export async function logWebSocketFallback(projectId: string, component: string, reason: string) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()
        
        // Отправляем кастомное событие в Яндекс Метрику
        ym(YMID, 'reachGoal', 'websocket_fallback', {
            projectId: projectId,
            component: component,
            reason: reason,
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint: fingerprint,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        })
        
        console.warn(`[YM Logged] WebSocket fallback: component=${component}, reason=${reason}, projectId=${projectId}, username=${user?.name || user?.email}`)
    } catch (e) {
        console.error('Ошибка логирования WebSocket fallback в Яндекс Метрику:', String(e))
    }
}

export async function logWebSocketSuccess(projectId: string, component: string) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()
        
        // Отправляем кастомное событие в Яндекс Метрику
        ym(YMID, 'reachGoal', 'websocket_success', {
            projectId: projectId,
            component: component,
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint: fingerprint,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href
        })
        
        console.log(`[YM Logged] WebSocket success: component=${component}, projectId=${projectId}, username=${user?.name || user?.email}`)
    } catch (e) {
        console.error('Ошибка логирования WebSocket success в Яндекс Метрику:', String(e))
    }
}

export async function logWebSocketCheckStage(
    projectId: string, 
    component: string, 
    stage: 'health_check_requested' | 'health_check_sent' | 'health_check_response' | 'health_check_timeout' | 'health_check_failed',
    details?: any
) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()
        
        // Отправляем кастомное событие в Яндекс Метрику
        ym(YMID, 'reachGoal', 'websocket_check_stage', {
            projectId: projectId,
            component: component,
            stage: stage,
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint: fingerprint,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
            ...details
        })
        
        console.log(`[YM Logged] WebSocket check stage: ${stage}, component=${component}, projectId=${projectId}`, details)
    } catch (e) {
        console.error('Ошибка логирования WebSocket check stage в Яндекс Метрику:', String(e))
    }
}

export async function logWebSocketPeriodicFailure(
    projectId: string,
    component: string,
    reason: string,
    details?: any
) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()

        ym(YMID, 'reachGoal', 'websocket_periodic_failure', {
            projectId: projectId,
            component: component,
            reason: reason,
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint: fingerprint,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            url: window.location.href,
            ...details,
        })
        console.warn(`[YM Logged] WebSocket periodic failure: component=${component}, projectId=${projectId}, reason=${reason}`, details)
    } catch (e) {
        console.error('Ошибка логирования WebSocket periodic failure в Яндекс Метрику:', String(e))
    }
}

// Открытие сайдбара настроек канала (вход в подключение/настройки коннектора).
// Цель в Метрике: connector_settings_opened (тип «JavaScript-событие»).
export async function logChannelSettingsOpened(connectorId: string, provider?: string | null) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()

        ym(YMID, 'reachGoal', 'connector_settings_opened', {
            connectorId,
            provider: provider ?? null,
            projectId: getProjectId(),
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint,
            timestamp: new Date().toISOString(),
            url: window.location.href,
        })

        console.log(`[YM Logged] connector_settings_opened: connectorId=${connectorId}, provider=${provider ?? 'unknown'}`)
    } catch (e) {
        console.error('Ошибка логирования connector_settings_opened в Яндекс Метрику:', String(e))
    }
}

// Этап подключения Telegram (dh-telegram). Цели в Метрике (тип «JavaScript-событие»):
// tg_qr_shown, tg_2fa_needed, tg_authorized.
export async function logTgConnectionStage(
    stage: 'qr_shown' | '2fa_needed' | 'authorized',
    payload: { connectorId: string },
) {
    try {
        if (typeof ym === 'undefined') {
            console.warn('Yandex Metrika is not loaded')
            return
        }

        const user = getInstance().user
        const fingerprint = await getFingerprint()

        ym(YMID, 'reachGoal', `tg_${stage}`, {
            connectorId: payload.connectorId,
            stage,
            projectId: getProjectId(),
            userId: user?.sub,
            username: user?.name || user?.email,
            role: user?.role,
            fingerprint,
            timestamp: new Date().toISOString(),
            url: window.location.href,
        })

        console.log(`[YM Logged] tg_${stage}: connectorId=${payload.connectorId}`)
    } catch (e) {
        console.error(`Ошибка логирования tg_${stage} в Яндекс Метрику:`, String(e))
    }
}

